// src/views/whatsappInbox/components/StatusLabelChips.tsx
import React, { memo } from 'react';
import './StatusLabelChips.css';

interface StatusLabelChipsProps {
  labels?: string[];
  status?: string[];
  userPreferences?: {
    labels: Array<{ value: string; name: string; color: string }>;
    status: Array<{ value: string; name: string; color: string }>;
  };
  maxVisible?: number;
  compact?: boolean;
}

/**
 * Component to display status and label chips with color coding
 * Optimized for mobile WhatsApp inbox card layout
 */
const StatusLabelChips: React.FC<StatusLabelChipsProps> = memo(({
  labels,
  status,
  userPreferences,
  maxVisible = 3,
  compact = false
}) => {
  // Ensure arrays are defined (handle undefined/null)
  const safeLabels = labels || [];
  const safeStatus = status || [];

  // Combine and map status and labels to their preferences
  const statusItems = safeStatus
    .filter(Boolean)
    .map(statusValue => {
      const pref = userPreferences?.status?.find(s => s.value === statusValue);
      return pref ? {
        type: 'status',
        value: statusValue,
        name: pref.name,
        color: pref.color || '#6366f1' // Default indigo color
      } : null;
    })
    .filter(Boolean) as Array<{ type: string; value: string; name: string; color: string }>;

  const labelItems = safeLabels
    .filter(Boolean)
    .map(labelValue => {
      const pref = userPreferences?.labels?.find(l => l.value === labelValue);
      return pref ? {
        type: 'label',
        value: labelValue,
        name: pref.name,
        color: pref.color || '#10b981' // Default green color
      } : null;
    })
    .filter(Boolean) as Array<{ type: string; value: string; name: string; color: string }>;

  // Combine both arrays
  const allChips = [...statusItems, ...labelItems];

  // If no chips to display, return null
  if (allChips.length === 0) return null;

  // Calculate visible and overflow chips
  const visibleChips = allChips.slice(0, maxVisible);
  const overflowCount = allChips.length - maxVisible;

  return (
    <div className={`status-label-chips ${compact ? 'compact' : ''}`}>
      {visibleChips.map((chip, index) => (
        <span
          key={`${chip.type}-${chip.value}-${index}`}
          className={`chip chip-${chip.type}`}
          style={{
            backgroundColor: `${chip.color}30`, // 30 = ~20% opacity - much bolder
            color: chip.color,
            borderColor: chip.color // Solid border for maximum visibility
          }}
          title={chip.name}
        >
          {chip.name}
        </span>
      ))}

      {overflowCount > 0 && (
        <span
          className="chip chip-overflow"
          title={`${overflowCount} more`}
        >
          +{overflowCount}
        </span>
      )}
    </div>
  );
});

StatusLabelChips.displayName = 'StatusLabelChips';

export default StatusLabelChips;
